import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ExpenseFormData, ExpenseCreateRequest, Category } from '../types';
import { useFormValidation } from '../hooks/useFormValidation';
import { getCurrencyDisplay, CURRENCIES } from '../config/currencies';
import CurrencySelect from './CurrencySelect';
import { sanitizeText, sanitizeNumber } from '../utils/sanitization';
import ToggleSwitch from './ToggleSwitch';

interface CreateExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (expense: ExpenseCreateRequest) => void;
  loading?: boolean;
  categories?: Category[];
  currencies?: string[];
  categoriesLoading?: boolean;
  hideWrapper?: boolean;
}

const CreateExpenseModal: React.FC<CreateExpenseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  categories = [],
  currencies = Object.keys(CURRENCIES),
  categoriesLoading = false,
  hideWrapper = false
}) => {
  const [formData, setFormData] = useState<ExpenseFormData>({
    name: '',
    amount: '',
    currency: 'USD',
    description: '',
    category_id: '',
    created_at: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    is_big_expense: false
  });

  const { errors, validate, clearError, clearAllErrors } = useFormValidation<ExpenseFormData>({
    name: {
      required: true,
      min: 1,
      max: 200
    },
    amount: {
      required: true,
      custom: (value) => {
        const num = sanitizeNumber(value);
        if (num === null || num <= 0) {
          return 'Please enter a valid positive number';
        }
        return undefined;
      }
    },
    category_id: { required: true },
    created_at: { required: true },
    currency: { required: true }
  });

  // Set first category as default when categories are loaded
  useEffect(() => {
    if (categories.length > 0 && !formData.category_id) {
      setFormData(prev => ({ ...prev, category_id: categories[0].id.toString() }));
    }
  }, [categories, formData.category_id]);

  const handleInputChange = (field: keyof ExpenseFormData, value: string) => {
    let sanitizedValue = value;

    // Apply sanitization based on field type
    if (field === 'name' || field === 'description') {
      sanitizedValue = sanitizeText(value, field === 'name' ? 200 : 500);
    } else if (field === 'amount') {
      // Allow decimal input during typing
      sanitizedValue = value.replace(/[^0-9.-]/g, '');
    }

    setFormData(prev => ({ ...prev, [field]: sanitizedValue }));
    clearError(field);
  };


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate(formData)) {
      return;
    }

    const expense: ExpenseCreateRequest = {
      name: formData.name.trim(),
      amount: parseFloat(formData.amount),
      currency: formData.currency,
      description: formData.description.trim() || undefined,
      category_id: parseInt(formData.category_id),
      created_at: new Date(formData.created_at).toISOString(),
      is_big_expense: formData.is_big_expense || false
    };

    onSubmit(expense);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        amount: '',
        currency: 'USD',
        description: '',
        category_id: '',
        created_at: new Date().toISOString().split('T')[0]
      });
      clearAllErrors();
      onClose();
    }
  };

  if (!isOpen) return null;

  const content = (
    <>
      <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <ToggleSwitch
              checked={formData.is_big_expense || false}
              onChange={(checked) => setFormData(prev => ({ ...prev, is_big_expense: checked }))}
              label="One-time big expense"
              disabled={loading}
            />

            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                type="text"
                className={`form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="e.g. Coffee at Starbucks"
                maxLength={150}
                disabled={loading}
              />
              {errors.name && <div className="text-error">{errors.name}</div>}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 'var(--spacing-md)' }}>
              <div className="form-group">
                <label className="form-label">Amount *</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={`form-input ${errors.amount ? 'error' : ''}`}
                  value={formData.amount}
                  onChange={(e) => handleInputChange('amount', e.target.value)}
                  placeholder="0.00"
                  disabled={loading}
                />
                {errors.amount && <div className="text-error">{errors.amount}</div>}
              </div>

              <div className="form-group">
                <label className="form-label">Currency</label>
                <CurrencySelect
                  value={formData.currency}
                  onChange={(val) => handleInputChange('currency', val)}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                className={`form-select ${errors.category_id ? 'error' : ''}`}
                value={formData.category_id}
                onChange={(e) => handleInputChange('category_id', e.target.value)}
                disabled={loading || categoriesLoading}
              >
                {categoriesLoading ? (
                  <option value="">Loading categories...</option>
                ) : (
                  <>
                    <option value="">Select a category</option>
                    {categories.map(category => {
                      const parent = category.parent_id
                        ? categories.find(c => c.id === category.parent_id)
                        : null;
                      return (
                        <option key={category.id} value={category.id.toString()}>
                          {category.name}{parent ? ` (${parent.name})` : ''}
                        </option>
                      );
                    })}
                  </>
                )}
              </select>
              {errors.category_id && <div className="text-error">{errors.category_id}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Date *</label>
              <input
                type="date"
                className={`form-input ${errors.created_at ? 'error' : ''}`}
                value={formData.created_at}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => handleInputChange('created_at', e.target.value)}
                disabled={loading}
              />
              {errors.created_at && <div className="text-error">{errors.created_at}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-input"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Optional notes about this expense"
                maxLength={350}
                rows={3}
                disabled={loading}
                style={{ resize: 'vertical', minHeight: '80px' }}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={handleClose}
              className="btn btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner" style={{ width: '16px', height: '16px' }} />
                  Adding...
                </>
              ) : (
                'Add Expense'
              )}
            </button>
          </div>
        </form>
    </>
  );

  if (hideWrapper) {
    return content;
  }

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="expense-modal" onClick={(e) => e.stopPropagation()}>
        {content}
      </div>
    </div>
  );
};

export default CreateExpenseModal;
